<script setup>
// NOTICE: KAPTION-INTEGRATION - WhatsApp Direct Send form page
// Sends a single template message to one contact (or new phone number)
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import useTemplateVariables from 'dashboard/composables/useTemplateVariables';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import ContactAPI from 'dashboard/api/contacts';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import NaturalLanguageDateTimePicker from 'dashboard/components-next/DateTimePicker/NaturalLanguageDateTimePicker.vue';
import KaptionMediaUpload from 'dashboard/components-next/Campaigns/KaptionMediaUpload.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { MEDIA_FORMATS } from 'dashboard/helper/templateHelper';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();

// ---------- Locked mode (coming from detail page) ----------
const lockedDirectId = ref(null);
const lockedCampaign = ref(null);
const isLocked = computed(() => !!lockedDirectId.value);

// ---------- Form state ----------
const formState = ref({
  inboxId: null,
  contactId: null,
  phoneNumber: '',
  templateId: null,
  variableBindings: {},
  allVariablesMapped: false,
  scheduledAt: null,
  shortenLinks: false,
  // NOTICE: KAPTION-INTEGRATION - Tappable card title (tap_target_configuration)
  tapTargetTitle: '',
});

const recipientMode = ref('contact');
const scheduleMode = ref('now');
const isSubmitting = ref(false);

// ---------- Store getters ----------
const getDirectMessageTemplates = useMapGetter(
  'inboxes/getDirectMessageTemplates'
);
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');

// ---------- Inbox options ----------
const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

// ---------- Template options ----------
// Direct-send template policy:
//   - `kaption_*` templates are reserved for system-managed flows
//     (reminders, scheduled messages, follow-ups) and are intentionally
//     hidden from this dropdown. Every other customer-managed template
//     is a first-class citizen regardless of category (UTILITY,
//     AUTHENTICATION, MARKETING).
const templateOptions = computed(() => {
  if (!formState.value.inboxId) return [];
  const templates = getDirectMessageTemplates.value(formState.value.inboxId);
  return (templates || [])
    .filter(tmpl => !tmpl.name.startsWith('kaption_'))
    .map(tmpl => {
      const friendlyName = tmpl.name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      const isPending = tmpl.status?.toLowerCase() === 'pending';
      const suffix = isPending ? ` - ${t('DIRECT.SEND.PENDING_BADGE')}` : '';
      return {
        value: tmpl.id,
        label: `${friendlyName} (${tmpl.language || 'en'})${suffix}`,
        template: tmpl,
      };
    });
});

// ---------- Selected template data ----------
const selectedTemplate = computed(() => {
  if (!formState.value.inboxId || !formState.value.templateId) return null;
  const templates = getDirectMessageTemplates.value(formState.value.inboxId);
  return (
    templates?.find(tmpl => tmpl.id === formState.value.templateId) || null
  );
});

const selectedInbox = computed(() =>
  inboxes.value?.find(i => i.id === formState.value.inboxId)
);

const templateText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = selectedTemplate.value.components.find(c => c.type === 'BODY');
  return body ? body.text : '';
});

// ---------- Header media detection ----------
const headerComponent = computed(() => {
  if (!selectedTemplate.value) return null;
  return selectedTemplate.value.components.find(c => c.type === 'HEADER');
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

// Detect URL buttons in the selected template — when present, link shortening
// is mandatory (Kaption click attribution requires kapt.io URLs).
const hasUrlButton = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttonsComp = selectedTemplate.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp) return false;
  return (buttonsComp.buttons || []).some(b => b.type === 'URL');
});

// `shortenLinks` is forced ON (and disabled) when the template has URL buttons.
const shortenLinksEffective = computed(
  () => hasUrlButton.value || formState.value.shortenLinks
);

const mediaHeaderType = computed(
  () => headerComponent.value?.format?.toLowerCase() || ''
);

const templateExampleMediaUrl = computed(() => {
  const hdr = headerComponent.value;
  if (!hdr) return '';
  return (
    hdr.example?.header_handle?.[0] ||
    hdr.example?.header_url?.[0] ||
    hdr.example?.[0] ||
    ''
  );
});

// Clone template with overridden header media for preview
const previewTemplate = computed(() => {
  if (!selectedTemplate.value) return null;
  const overrideUrl = formState.value.variableBindings?.header?.media_url;
  if (!overrideUrl || !hasMediaHeader.value) return selectedTemplate.value;
  const components = selectedTemplate.value.components.map(c => {
    if (c.type !== 'HEADER') return c;
    return {
      ...c,
      example: { ...c.example, header_handle: [overrideUrl] },
    };
  });
  return { ...selectedTemplate.value, components };
});

// ---------- Contact search ----------
const contactSearchResults = ref([]);
let searchTimeout = null;

const handleContactSearch = query => {
  clearTimeout(searchTimeout);
  if (!query || query.length < 2) {
    contactSearchResults.value = [];
    return;
  }
  searchTimeout = setTimeout(async () => {
    try {
      const { data } = await ContactAPI.search(query, 1, 'name');
      contactSearchResults.value = (data.payload || []).map(c => ({
        value: c.id,
        label: `${c.name || ''} (${c.phone_number || c.email || ''})`.trim(),
        contact: c,
      }));
    } catch {
      contactSearchResults.value = [];
    }
  }, 300);
};

const selectedContact = computed(() => {
  if (recipientMode.value !== 'contact') return null;
  return (
    contactSearchResults.value.find(c => c.value === formState.value.contactId)
      ?.contact || null
  );
});

// ---------- Template variables ----------
const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;

const templateVariables = computed(() => {
  if (!templateText.value) return [];
  const matches = Array.from(templateText.value.matchAll(VARIABLE_PATTERN));
  const keys = matches.map(m => m[1].trim());
  return [...new Set(keys)];
});

// SID: direct-send.BUTTON_VARS.1 — surface URL/COPY_CODE button placeholders
// as fillable inputs so the user can supply them at send time. Without this,
// templates whose button URL contains `{{1}}` would silently 422 at
// KaptionDirectValidationService#validate_button_params.
const buttonVariables = computed(() => {
  if (!selectedTemplate.value) return [];
  const buttonsComp = selectedTemplate.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp) return [];
  return (buttonsComp.buttons || [])
    .map((btn, index) => ({ btn, index }))
    .filter(({ btn }) => {
      if (btn.type === 'URL') return (btn.url || '').includes('{{');
      if (btn.type === 'COPY_CODE') return true;
      return false;
    })
    .map(({ btn, index }) => ({
      index,
      type: btn.type, // 'URL' | 'COPY_CODE'
      label: btn.text || `Button ${index + 1}`,
      urlPreview: btn.url || '',
      example: Array.isArray(btn.example) ? btn.example[0] : undefined,
    }));
});

// Variable suggestions for the editor
const { variableSuggestions } = useTemplateVariables({
  sampleContacts: computed(() => {
    const contact = selectedContact.value;
    return contact ? [contact] : [];
  }),
});

const availableBindingsForSlot = computed(() => {
  const suggestions = variableSuggestions.value || [];
  return suggestions
    .filter(s => s.key?.startsWith('contact.'))
    .map(s => ({
      value: `{{ ${s.key} }}`,
      label: s.label,
    }));
});

const iframeVariables = computed(() => {
  const bindings = formState.value.variableBindings?.body || {};
  return templateVariables.value.map(slot => ({
    slot,
    binding: bindings[slot] || null,
    availableBindings: availableBindingsForSlot.value,
  }));
});

// ---------- Resolved phone number ----------
const resolvedPhoneNumber = computed(() => {
  if (recipientMode.value === 'phone') return formState.value.phoneNumber;
  return selectedContact.value?.phone_number || '';
});

// ---------- Resolved values for preview ----------
const resolvedValues = computed(() => {
  const contact = selectedContact.value;
  if (!contact) return {};
  const values = {};
  values['{{ contact.name }}'] = contact.name || '';
  values['{{ contact.first_name }}'] =
    contact.name?.split(' ')[0] || contact.name || '';
  values['{{ contact.last_name }}'] =
    contact.name?.split(' ').slice(1).join(' ') || '';
  values['{{ contact.email }}'] = contact.email || '';
  values['{{ contact.phone_number }}'] = contact.phone_number || '';
  if (contact.custom_attributes) {
    Object.entries(contact.custom_attributes).forEach(([key, val]) => {
      values[`{{ contact.custom_attribute.${key} }}`] = String(val);
    });
  }
  return values;
});

// ---------- Header media update ----------
const updateHeaderMediaUrl = value => {
  formState.value.variableBindings = {
    ...formState.value.variableBindings,
    header: { media_url: value, media_type: mediaHeaderType.value },
  };
};

// ---------- Variable binding from iframe ----------
const checkAllVariablesMapped = () => {
  const bodyBindings = formState.value.variableBindings?.body || {};
  const bodyOk =
    templateVariables.value.length === 0 ||
    templateVariables.value.every(
      varKey => bodyBindings[varKey] && bodyBindings[varKey].trim() !== ''
    );

  const buttonBindings = formState.value.variableBindings?.buttons || [];
  const buttonsOk =
    buttonVariables.value.length === 0 ||
    buttonVariables.value.every(({ index }) => {
      const entry = buttonBindings[index];
      const value = entry?.parameter;
      return typeof value === 'string' && value.trim() !== '';
    });

  formState.value.allVariablesMapped = bodyOk && buttonsOk;
};

const handleBindingsUpdate = bindings => {
  const newBody = {};
  bindings.forEach(({ slot, binding }) => {
    if (binding) newBody[slot] = binding;
  });
  formState.value.variableBindings = {
    ...formState.value.variableBindings,
    body: newBody,
  };
  checkAllVariablesMapped();
};

// NOTICE: KAPTION-INTEGRATION - Explicit variable input fields for locked mode
const updateVariableValue = (varName, value) => {
  const currentBody = { ...(formState.value.variableBindings?.body || {}) };
  currentBody[varName] = value;
  formState.value.variableBindings = {
    ...formState.value.variableBindings,
    body: currentBody,
  };
};

// SID: direct-send.BUTTON_VARS.1 — write a button-param entry that mirrors the
// shape KaptionDirectService expects: `{ type, parameter }` keyed by button
// index. Sparse indices (skipped buttons) are filled with nulls so the array
// length matches the template's BUTTONS component, which validate_button_params
// indexes positionally.
const updateButtonVariable = (index, type, value) => {
  const current = [...(formState.value.variableBindings?.buttons || [])];
  while (current.length <= index) current.push(null);
  current[index] = { type: type.toLowerCase(), parameter: value };
  formState.value.variableBindings = {
    ...formState.value.variableBindings,
    buttons: current,
  };
};

const openSuggestionDropdown = ref(null);
const toggleSuggestionDropdown = varName => {
  openSuggestionDropdown.value =
    openSuggestionDropdown.value === varName ? null : varName;
};

const insertContactField = (varName, fieldValue) => {
  updateVariableValue(varName, fieldValue);
  openSuggestionDropdown.value = null;
};

// ---------- Tab configs ----------
const recipientTabs = computed(() => [
  { label: t('DIRECT.SEND.CONTACT_TAB'), value: 'contact' },
  { label: t('DIRECT.SEND.PHONE_TAB'), value: 'phone' },
]);

const scheduleTabs = computed(() => [
  { label: t('DIRECT.SEND.SEND_NOW_TAB'), value: 'now' },
  { label: t('DIRECT.SEND.SCHEDULE_TAB'), value: 'schedule' },
]);

const recipientTabIndex = computed(() =>
  recipientMode.value === 'contact' ? 0 : 1
);
const scheduleTabIndex = computed(() => (scheduleMode.value === 'now' ? 0 : 1));

const handleRecipientTabChange = tab => {
  recipientMode.value = tab.value;
};
const handleScheduleTabChange = tab => {
  scheduleMode.value = tab.value;
};

// ---------- Validation ----------
const isFormValid = computed(() => {
  const hasInbox = !!formState.value.inboxId;
  const hasRecipient = resolvedPhoneNumber.value.length > 5;
  const hasTemplate = !!formState.value.templateId;
  const variablesOk =
    templateVariables.value.length === 0 || formState.value.allVariablesMapped;
  const scheduleOk =
    scheduleMode.value === 'now' || !!formState.value.scheduledAt;
  return (
    // feature-permissions.CAMPAIGNS.1
    canUseFeature('whatsapp_direct_send') &&
    hasInbox &&
    hasRecipient &&
    hasTemplate &&
    variablesOk &&
    scheduleOk
  );
});

const sendButtonLabel = computed(() =>
  scheduleMode.value === 'schedule'
    ? t('DIRECT.SEND.SCHEDULE_BUTTON')
    : t('DIRECT.SEND.SEND_BUTTON')
);

// ---------- Submit ----------
const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    const accountId = route.params.accountId;
    const template = selectedTemplate.value;

    // Build processed_params (same cleaning as campaign builder)
    const processedParams = {};
    const bodyBindings = formState.value.variableBindings?.body;
    if (bodyBindings) {
      processedParams.body = {};
      Object.entries(bodyBindings).forEach(([key, value]) => {
        const cleanKey = key.replace(/^{{|}}$/g, '').trim();
        processedParams.body[cleanKey] = value;
      });
    }
    if (formState.value.variableBindings?.header) {
      processedParams.header = {
        ...formState.value.variableBindings.header,
      };
    }
    if (formState.value.variableBindings?.buttons) {
      processedParams.buttons = [...formState.value.variableBindings.buttons];
    }

    const payload = {
      inbox_id: formState.value.inboxId,
      template_name: template.name,
      template_language: template.language,
      phone_number: resolvedPhoneNumber.value,
      processed_params: processedParams,
      create_conversation: false,
      shorten_links: shortenLinksEffective.value,
      // NOTICE: KAPTION-INTEGRATION - Card title for tappable template card;
      // backend treats blank as no-op and falls back to campaign defaults.
      tap_target_title: formState.value.tapTargetTitle?.trim() || null,
      scheduled_at:
        scheduleMode.value === 'schedule' && formState.value.scheduledAt
          ? new Date(formState.value.scheduledAt).toISOString()
          : null,
    };

    // If coming from a detail page, send via the existing campaign
    const sendUrl = lockedDirectId.value
      ? `/api/v1/accounts/${accountId}/direct_messages/${lockedDirectId.value}/send_message`
      : `/api/v1/accounts/${accountId}/direct_messages/send_message`;

    await window.axios.post(sendUrl, payload);

    const successKey =
      scheduleMode.value === 'schedule'
        ? 'DIRECT.SEND.SUCCESS_SCHEDULED'
        : 'DIRECT.SEND.SUCCESS_SENT';
    useAlert(t(successKey));
    router.push({ name: 'campaigns_direct_index' });
  } catch (err) {
    const data = err?.response?.data;
    if (data?.error === 'template_pending') {
      useAlert(
        t('DIRECT.SEND.TEMPLATE_PENDING_MESSAGE', {
          minutes: data.retry_after_minutes || 5,
        })
      );
    } else {
      useAlert(t('DIRECT.SEND.ERROR'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  if (lockedDirectId.value) {
    router.push({
      name: 'campaigns_direct_detail',
      params: { directId: lockedDirectId.value },
    });
  } else {
    router.push({ name: 'campaigns_direct_index' });
  }
};

const navigateToCreateTemplate = () => {
  router.push({
    name: 'campaigns_whatsapp_templates_new',
    query: { returnTo: 'direct_send', inboxId: formState.value.inboxId },
  });
};

// ---------- Watchers ----------
// Reset template + bindings when inbox changes (skip in locked mode)
watch(
  () => formState.value.inboxId,
  () => {
    if (isLocked.value) return;
    formState.value.templateId = null;
    formState.value.variableBindings = {};
    formState.value.allVariablesMapped = false;
  }
);

// Reset bindings when template changes, pre-fill header media (skip in locked mode)
watch(
  () => formState.value.templateId,
  newId => {
    if (isLocked.value) return;
    const newBindings = {};
    if (newId && hasMediaHeader.value && templateExampleMediaUrl.value) {
      newBindings.header = {
        media_url: templateExampleMediaUrl.value,
        media_type: mediaHeaderType.value,
      };
    }
    formState.value.variableBindings = newBindings;
    formState.value.allVariablesMapped = false;
    checkAllVariablesMapped();
  }
);

// Re-check variable mapping when bindings change
watch(
  () => formState.value.variableBindings,
  () => checkAllVariablesMapped(),
  { deep: true }
);

// ---------- Init ----------
// Pre-fill from locked campaign when directId query param is present
const initFromCampaign = async () => {
  const qDirectId = route.query.directId;
  if (!qDirectId) return;

  try {
    const accountId = route.params.accountId;
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId}/direct_messages/${qDirectId}`
    );
    const camp = response.data;
    lockedDirectId.value = qDirectId;
    lockedCampaign.value = camp;

    // Set inbox first
    formState.value.inboxId = camp.inbox_id;

    // Wait for templates to load, then match template by name+language
    const waitForTemplates = () => {
      const templates = getDirectMessageTemplates.value(camp.inbox_id) || [];
      const match = templates.find(
        tmpl =>
          tmpl.name === camp.template_params?.name &&
          tmpl.language === camp.template_params?.language
      );
      if (match) {
        formState.value.templateId = match.id;

        // Pre-fill variable bindings from campaign defaults
        const defaultParams = camp.template_params?.processed_params;
        if (defaultParams) {
          const newBindings = {};
          if (defaultParams.body) {
            newBindings.body = {};
            Object.entries(defaultParams.body).forEach(([key, value]) => {
              newBindings.body[key] = value;
            });
          }
          if (defaultParams.header) {
            newBindings.header = { ...defaultParams.header };
          }
          if (defaultParams.buttons) {
            newBindings.buttons = [...defaultParams.buttons];
          }
          formState.value.variableBindings = newBindings;
        }

        checkAllVariablesMapped();
      }
    };

    // Templates may not be loaded yet — retry briefly
    waitForTemplates();
    if (!formState.value.templateId) {
      const unwatch = watch(
        () => getDirectMessageTemplates.value(camp.inbox_id),
        () => {
          waitForTemplates();
          if (formState.value.templateId) unwatch();
        }
      );
    }
  } catch {
    // If campaign fetch fails, fall back to normal (unlocked) mode
    lockedDirectId.value = null;
  }
};

store.dispatch('inboxes/get');

onMounted(() => {
  initFromCampaign();
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background -mx-6">
    <!-- Header -->
    <header
      class="relative flex items-center px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleCancel"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">{{ t('DIRECT.SEND.BACK') }}</span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ t('DIRECT.SEND.TITLE') }}
      </h1>
    </header>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto px-8 py-6">
      <div class="grid grid-cols-5 gap-6">
        <!-- Left Column: Form (3/5) -->
        <div class="col-span-3 flex flex-col gap-6 pr-2">
          <!-- Inbox Selection -->
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.INBOX_LABEL') }}
            </label>
            <ComboBox
              v-model="formState.inboxId"
              :options="inboxOptions"
              :placeholder="t('DIRECT.SEND.INBOX_PLACEHOLDER')"
              :disabled="isLocked"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
          </div>

          <!-- Recipient -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('DIRECT.SEND.RECIPIENT_LABEL') }}
              </label>
              <TabBar
                :tabs="recipientTabs"
                :initial-active-tab="recipientTabIndex"
                @tab-changed="handleRecipientTabChange"
              />
            </div>

            <!-- Contact Search Mode -->
            <ComboBox
              v-if="recipientMode === 'contact'"
              v-model="formState.contactId"
              :options="contactSearchResults"
              :placeholder="t('DIRECT.SEND.SEARCH_CONTACT_PLACEHOLDER')"
              use-api-results
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              @search="handleContactSearch"
            />

            <!-- Phone Number Mode -->
            <PhoneNumberInput
              v-else
              v-model="formState.phoneNumber"
              :placeholder="t('DIRECT.SEND.PHONE_PLACEHOLDER')"
            />
          </div>

          <!-- Template Selection -->
          <div v-if="!isLocked" class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.TEMPLATE_LABEL') }}
            </label>
            <ComboBox
              v-model="formState.templateId"
              :options="templateOptions"
              :placeholder="t('DIRECT.SEND.TEMPLATE_PLACEHOLDER')"
              :disabled="!formState.inboxId"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
            <button
              type="button"
              class="text-xs text-n-brand hover:text-n-brand-hover flex items-center gap-1 mt-1"
              @click="navigateToCreateTemplate"
            >
              <span class="i-lucide-plus w-3 h-3" />
              {{ t('DIRECT.CREATE_TEMPLATE') }}
            </button>
          </div>

          <!-- Pending template warning -->
          <div
            v-if="selectedTemplate?.status?.toLowerCase() === 'pending'"
            class="flex items-center gap-2 px-4 py-3 rounded-lg bg-kpt-amber/10 text-kpt-amber text-sm"
          >
            <span class="i-lucide-clock w-4 h-4 shrink-0" />
            {{ t('DIRECT.SEND.TEMPLATE_PENDING_WARNING') }}
          </div>

          <!-- Header Media Upload -->
          <div
            v-if="hasMediaHeader && selectedTemplate"
            class="flex flex-col gap-1"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{
                t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_FORMAT_LABEL', {
                  format:
                    mediaHeaderType.charAt(0).toUpperCase() +
                    mediaHeaderType.slice(1),
                })
              }}
            </label>
            <KaptionMediaUpload
              :model-value="formState.variableBindings?.header?.media_url || ''"
              :media-type="mediaHeaderType"
              @update:model-value="updateHeaderMediaUrl"
            />
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Explicit variable inputs for body variables -->
          <div
            v-if="templateVariables.length > 0 && selectedTemplate"
            class="flex flex-col gap-3"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.VARIABLES_SECTION_LABEL') }}
            </label>
            <p class="-mt-2 mb-0 text-xs text-n-slate-11">
              {{ t('DIRECT.SEND.ALL_VARIABLES_REQUIRED_HINT') }}
            </p>
            <div
              v-for="varName in templateVariables"
              :key="varName"
              class="flex flex-col gap-1"
            >
              <label class="text-xs font-medium text-n-slate-11">
                {{ varName }}
              </label>
              <div class="flex items-center gap-2">
                <Input
                  :model-value="
                    formState.variableBindings?.body?.[varName] || ''
                  "
                  type="text"
                  class="flex-1"
                  :placeholder="
                    t('DIRECT.SEND.VARIABLE_INPUT_PLACEHOLDER', {
                      variable: varName,
                    })
                  "
                  @update:model-value="
                    value => updateVariableValue(varName, value)
                  "
                />
                <div
                  v-if="availableBindingsForSlot.length > 0"
                  class="relative"
                >
                  <button
                    type="button"
                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 transition-colors"
                    :title="t('DIRECT.SEND.VARIABLE_SUGGESTION_BUTTON_TITLE')"
                    @click="toggleSuggestionDropdown(varName)"
                  >
                    <span class="i-lucide-contact w-4 h-4 text-n-slate-11" />
                  </button>
                  <div
                    v-if="openSuggestionDropdown === varName"
                    class="absolute right-0 top-full z-10 mt-1 w-56 rounded-lg bg-n-solid-2 shadow-lg border border-n-weak py-1"
                  >
                    <button
                      v-for="suggestion in availableBindingsForSlot"
                      :key="suggestion.value"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-black2 transition-colors"
                      @click="insertContactField(varName, suggestion.value)"
                    >
                      {{ suggestion.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Explicit inputs for URL / COPY_CODE
               button parameters. SID: direct-send.BUTTON_VARS.1 -->
          <div
            v-if="buttonVariables.length > 0 && selectedTemplate"
            class="flex flex-col gap-3"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.BUTTON_VARIABLES_SECTION_LABEL') }}
            </label>
            <div
              v-for="btn in buttonVariables"
              :key="btn.index"
              class="flex flex-col gap-1"
            >
              <label class="text-xs font-medium text-n-slate-11">
                {{ btn.label }}
                <span class="text-n-slate-9">— {{ btn.type }}</span>
              </label>
              <p
                v-if="btn.type === 'URL'"
                class="-mt-1 text-xs text-n-slate-10 truncate"
                :title="btn.urlPreview"
              >
                {{ btn.urlPreview }}
              </p>
              <Input
                :model-value="
                  formState.variableBindings?.buttons?.[btn.index]?.parameter ||
                  ''
                "
                type="text"
                :placeholder="
                  btn.example ||
                  t('DIRECT.SEND.BUTTON_VARIABLE_INPUT_PLACEHOLDER', {
                    button: btn.label,
                  })
                "
                @update:model-value="
                  value => updateButtonVariable(btn.index, btn.type, value)
                "
              />
            </div>
          </div>

          <!-- Schedule -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('DIRECT.SEND.SCHEDULE_LABEL') }}
              </label>
              <TabBar
                :tabs="scheduleTabs"
                :initial-active-tab="scheduleTabIndex"
                @tab-changed="handleScheduleTabChange"
              />
            </div>
            <NaturalLanguageDateTimePicker
              v-if="scheduleMode === 'schedule'"
              v-model="formState.scheduledAt"
            />
          </div>

          <!-- Shorten links via kapt.io -->
          <div v-if="selectedTemplate" class="flex flex-col gap-1">
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="shortenLinksEffective"
                :disabled="hasUrlButton"
                class="h-4 w-4 rounded border-n-weak"
                @change="formState.shortenLinks = $event.target.checked"
              />
              {{ t('DIRECT.SEND.SHORTEN_LINKS_LABEL') }}
            </label>
            <p class="text-xs text-n-slate-11 ml-6">
              <template v-if="hasUrlButton">
                {{ t('DIRECT.SEND.SHORTEN_LINKS_FORCED_HINT') }}
              </template>
              <template v-else>
                {{ t('DIRECT.SEND.SHORTEN_LINKS_HINT') }}
              </template>
            </p>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Card title (tap_target_configuration).
               Renders the template as a tappable card whose whole surface opens
               the tracked link. Only shown for templates with a URL button. -->
          <div v-if="hasUrlButton" class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.TAP_TARGET_TITLE_LABEL') }}
            </label>
            <Input
              v-model="formState.tapTargetTitle"
              type="text"
              :placeholder="t('DIRECT.SEND.TAP_TARGET_TITLE_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-11">
              {{ t('DIRECT.SEND.TAP_TARGET_TITLE_HINT') }}
            </p>
          </div>
        </div>

        <!-- Right Column: Preview (2/5) -->
        <div class="col-span-2 flex flex-col gap-3">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('DIRECT.SEND.PREVIEW_TITLE') }}
          </h3>

          <KaptionWhatsAppPreviewEnhanced
            v-if="templateText"
            :template="previewTemplate"
            :variables="iframeVariables"
            :resolved-values="resolvedValues"
            :tap-target-title="formState.tapTargetTitle"
            mode="edit"
            :loading="false"
            :bare="false"
            :company-name="selectedInbox?.name || 'Business'"
            class="w-full h-[520px]"
            @bindings-update="handleBindingsUpdate"
          />

          <KaptionWhatsAppPreviewEnhanced
            v-else
            :template="null"
            :loading="false"
            :bare="false"
            mode="render"
            class="w-full h-[520px]"
          />
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="flex items-center justify-between px-8 py-4 border-t border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:text-n-slate-12 px-4 py-2.5 rounded-lg hover:bg-n-alpha-black2 transition-colors"
        @click="handleCancel"
      >
        {{ t('DIALOG.BUTTONS.CANCEL') }}
      </button>
      <!-- feature-permissions.CAMPAIGNS.1 — isFormValid includes canUseFeature('whatsapp_direct_send') -->
      <button
        type="button"
        class="px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        :class="[
          isFormValid
            ? 'text-white bg-n-brand hover:bg-n-brand-hover shadow-sm'
            : 'bg-n-slate-3 text-n-slate-10 cursor-not-allowed',
        ]"
        :disabled="!isFormValid || isSubmitting"
        @click="handleSubmit"
      >
        <span
          v-if="isSubmitting"
          class="animate-spin h-4 w-4 border-2 border-white border-r-transparent rounded-full"
        />
        <span class="i-lucide-send w-4 h-4" />
        {{ sendButtonLabel }}
      </button>
    </footer>
  </div>
</template>
